import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/common/LanguageSelector';
import { Search, Menu, X, Shield, Heart, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFavorites } from '@/contexts/FavoritesContext';

export default function Header() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showFavPanel, setShowFavPanel] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const { favoriteTeams, toggleFavorite, notifications, unreadCount, markAllRead, clearNotifications } = useFavorites();
  const { t } = useTranslation();
  const { currentLang, getLocalizedPath } = useLanguage();

  const navLinks = [
    { path: `/${currentLang}`, label: t('nav.home') },
    { path: `/${currentLang}/${currentLang === 'tr' ? 'futbol' : 'football'}`, label: t('nav.football') },
    { path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026' : 'world-cup-2026'}`, label: t('nav.worldCup', 'World Cup'), special: true },
    { path: `/${currentLang}/${currentLang === 'tr' ? 'kupalar' : 'cups'}`, label: currentLang === 'tr' ? 'Kupalar' : 'Cups' },
    { path: `/${currentLang}/bookmakers`, label: t('nav.bookmakers', 'Bookmakers') },
    { path: getLocalizedPath('valueBets'), label: t('nav.valueBets') },
    { path: getLocalizedPath('surebets'), label: t('nav.surebets') },
  ];

  return (
    <header data-testid="main-header" className="sticky top-0 z-[60] backdrop-blur-md bg-slate-950/90 border-b border-slate-800">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={`/${currentLang}`} className="flex items-center shrink-0" data-testid="logo-link">
            <img src="/oddsis-logo-dark.png" alt="ODDSIS" style={{ height: '46px', width: 'auto' }} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-3 py-1.5 rounded-md text-sm font-bold whitespace-nowrap transition-colors ${
                  location.pathname.startsWith(link.path) && link.path !== `/${currentLang}`
                    ? (link.special ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-emerald-400')
                    : location.pathname === link.path
                    ? 'bg-slate-800 text-emerald-400'
                    : link.special
                    ? 'text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}>
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Favorites + Notifications + Admin + Mobile */}
          <div className="flex items-center gap-1">

            {/* Favorites */}
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => { setShowFavPanel(!showFavPanel); setShowNotifPanel(false); }}
                className={`text-slate-400 hover:text-slate-200 relative ${favoriteTeams.length > 0 ? 'text-red-400' : ''}`}
                data-testid="favorites-toggle">
                <Heart className={`w-4 h-4 ${favoriteTeams.length > 0 ? 'fill-current' : ''}`} />
                {favoriteTeams.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                    {favoriteTeams.length}
                  </span>
                )}
              </Button>
              {showFavPanel && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 p-3" data-testid="favorites-panel">
                  <p className="text-xs font-semibold text-slate-300 mb-2">{t('favoritesPanel.title', 'Favorite Teams')}</p>
                  {favoriteTeams.length > 0 ? (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {favoriteTeams.map(t => (
                        <div key={t.id} className="flex items-center justify-between text-xs py-1">
                          <span className="text-slate-300">{t.name}</span>
                          <button onClick={() => toggleFavorite(t.id, t.name)} className="text-red-400 hover:text-red-300">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 py-2 text-center">{t('favoritesPanel.empty', 'Click hearts on match cards to add favorites')}</p>
                  )}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => { setShowNotifPanel(!showNotifPanel); setShowFavPanel(false); if (unreadCount > 0) markAllRead(); }}
                className="text-slate-400 hover:text-slate-200 relative" data-testid="notifications-toggle">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>
              {showNotifPanel && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 p-3" data-testid="notifications-panel">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-300">{t('notificationsPanel.title', 'Notifications')}</p>
                    {notifications.length > 0 && (
                      <button onClick={clearNotifications} className="text-[10px] text-slate-500 hover:text-slate-400">{t('notificationsPanel.clearAll', 'Clear all')}</button>
                    )}
                  </div>
                  {notifications.length > 0 ? (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {notifications.slice(0, 10).map(n => (
                        <div key={n.id} className={`text-xs p-2 rounded ${n.read ? 'text-slate-500' : 'text-slate-300 bg-slate-800/50'}`}>
                          <p>{n.message}</p>
                          <p className="text-[10px] text-slate-600 mt-0.5">{new Date(n.time).toLocaleTimeString()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 py-2 text-center">{t('notificationsPanel.empty', 'No notifications yet')}</p>
                  )}
                </div>
              )}
            </div>

            <a href="/admin" data-testid="admin-link">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200">
                <Shield className="w-4 h-4" />
              </Button>
            </a>
            <LanguageSelector />
            <Button variant="ghost" size="sm" className="md:hidden text-slate-400"
              onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="md:hidden pb-4 border-t border-slate-800 pt-3 fade-in" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2 rounded-md text-sm font-bold ${
                  location.pathname === link.path ? 'bg-slate-800 text-emerald-400' : 'text-slate-400 hover:text-slate-200'
                }`}>
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
